import { useCallback, useState } from 'react';

export interface ResourceMeta {
  starred: boolean;
  openCount: number;
  lastOpenedAt: number;
}

type MetaStore = Record<string, ResourceMeta>;

const STORE_KEY = 'careweave_resource_meta';

function loadStore(): MetaStore {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function saveStore(store: MetaStore) {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

const DEFAULT_META: ResourceMeta = { starred: false, openCount: 0, lastOpenedAt: 0 };

export function useResourceMeta() {
  const [store, setStore] = useState<MetaStore>(loadStore);

  const getMeta = useCallback(
    (id: string): ResourceMeta => store[id] ?? DEFAULT_META,
    [store],
  );

  const toggleStar = useCallback((id: string) => {
    setStore(prev => {
      const meta = prev[id] ?? DEFAULT_META;
      const next = { ...prev, [id]: { ...meta, starred: !meta.starred } };
      saveStore(next);
      return next;
    });
  }, []);

  const recordOpen = useCallback((id: string) => {
    setStore(prev => {
      const meta = prev[id] ?? DEFAULT_META;
      const next = {
        ...prev,
        [id]: { ...meta, openCount: meta.openCount + 1, lastOpenedAt: Date.now() },
      };
      saveStore(next);
      return next;
    });
  }, []);

  return { getMeta, toggleStar, recordOpen };
}
